import React, { createContext, useContext, useState, useEffect } from 'react';

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Master Data
  const [courts, setCourts] = useState([]);
  const [judges, setJudges] = useState([]);
  const [advocates, setAdvocates] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Case Data
  const [cases, setCases] = useState([]);
  const [hearings, setHearings] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [judgments, setJudgments] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);

  // Initialize mock data
  const initializeMockData = () => {
    const mockCourts = [
      {
        id: '1',
        courtName: 'Supreme Court of Justice',
        courtType: 'Supreme Court',
        jurisdiction: 'National',
        address: '1 Constitution Avenue, Capital City',
        contactNumber: '+1 (555) 100-0001',
        email: 'supreme@ecourt.gov',
        courtrooms: [],
        createdAt: new Date().toISOString(),
      },
      {
        id: '2',
        courtName: 'High Court - Eastern District',
        courtType: 'High Court',
        jurisdiction: 'Eastern Region',
        address: '500 Justice Boulevard, Eastern City',
        contactNumber: '+1 (555) 200-0002',
        email: 'highcourt.east@ecourt.gov',
        courtrooms: [],
        createdAt: new Date().toISOString(),
      },
      {
        id: '3',
        courtName: 'District Court - Central',
        courtType: 'District Court',
        jurisdiction: 'Central District',
        address: '123 Legal Street, Central City',
        contactNumber: '+1 (555) 300-0003',
        email: 'district.central@ecourt.gov',
        courtrooms: [],
        createdAt: new Date().toISOString(),
      },
    ];

    const mockJudges = [
      {
        id: '1',
        judgeName: 'Hon. Justice Robert Smith',
        designation: 'Chief Justice',
        courtId: '1',
        courtName: 'Supreme Court of Justice',
        benchName: 'Bench A',
        email: 'r.smith@ecourt.gov',
        contactNumber: '+1 (555) 101-0001',
        joiningDate: '2020-01-15',
        tenure: '10',
        createdAt: new Date().toISOString(),
      },
      {
        id: '2',
        judgeName: 'Hon. Justice Emily Johnson',
        designation: 'Justice',
        courtId: '1',
        courtName: 'Supreme Court of Justice',
        benchName: 'Bench B',
        email: 'e.johnson@ecourt.gov',
        contactNumber: '+1 (555) 101-0002',
        joiningDate: '2021-03-20',
        tenure: '8',
        createdAt: new Date().toISOString(),
      },
      {
        id: '3',
        judgeName: 'Hon. Judge Michael Brown',
        designation: 'District Judge',
        courtId: '3',
        courtName: 'District Court - Central',
        benchName: 'Courtroom 1',
        email: 'm.brown@ecourt.gov',
        contactNumber: '+1 (555) 301-0003',
        joiningDate: '2019-06-10',
        tenure: '12',
        createdAt: new Date().toISOString(),
      },
    ];

    const mockAdvocates = [
      {
        id: '1',
        advocateName: 'John Williams',
        barEnrollmentNumber: 'BAR-2020-001',
        barCouncil: 'State Bar Council',
        specialization: 'Criminal Law',
        email: 'j.williams@lawfirm.com',
        contactNumber: '+1 (555) 401-0001',
        address: '100 Lawyer Avenue, Legal City',
        experience: '15',
        createdAt: new Date().toISOString(),
      },
      {
        id: '2',
        advocateName: 'Sarah Davis',
        barEnrollmentNumber: 'BAR-2018-045',
        barCouncil: 'State Bar Council',
        specialization: 'Civil Law',
        email: 's.davis@lawfirm.com',
        contactNumber: '+1 (555) 401-0002',
        address: '200 Attorney Street, Legal City',
        experience: '12',
        createdAt: new Date().toISOString(),
      },
      {
        id: '3',
        advocateName: 'David Wilson',
        barEnrollmentNumber: 'BAR-2019-078',
        barCouncil: 'State Bar Council',
        specialization: 'Family Law',
        email: 'd.wilson@lawfirm.com',
        contactNumber: '+1 (555) 401-0003',
        address: '300 Counsel Road, Legal City',
        experience: '8',
        createdAt: new Date().toISOString(),
      },
    ];

    const mockUsers = [
      {
        id: '1',
        username: 'admin',
        email: 'admin@ecourt.gov',
        password: 'admin123',
        role: 'Admin',
        fullName: 'System Administrator',
        contactNumber: '+1 (555) 001-0001',
        isActive: true,
        createdAt: new Date().toISOString(),
      },
      {
        id: '2',
        username: 'clerk1',
        email: 'clerk1@ecourt.gov',
        password: 'clerk123',
        role: 'Clerk',
        fullName: 'Court Clerk One',
        contactNumber: '+1 (555) 002-0002',
        isActive: true,
        createdAt: new Date().toISOString(),
      },
    ];

    const mockCases = [
      {
        id: '1',
        caseNumber: 'CIV/2024/0001',
        caseType: 'Civil',
        caseTitle: 'Property Dispute - ABC vs XYZ',
        filingDate: '2024-01-15',
        courtId: '3',
        courtName: 'District Court - Central',
        judgeId: '3',
        judgeName: 'Hon. Judge Michael Brown',
        description: 'Property boundary dispute between two parties',
        status: 'In Progress',
        parties: [
          {
            id: '1',
            partyName: 'ABC Corporation',
            partyType: 'Plaintiff',
            address: '123 Business Street',
            contactNumber: '+1 (555) 501-0001',
            email: 'abc@corp.com',
            advocateId: '2',
            advocateName: 'Sarah Davis',
          },
          {
            id: '2',
            partyName: 'XYZ Industries',
            partyType: 'Defendant',
            address: '456 Industry Avenue',
            contactNumber: '+1 (555) 501-0002',
            email: 'xyz@ind.com',
            advocateId: '1',
            advocateName: 'John Williams',
          },
        ],
        advocateMappings: [
          {
            id: '1',
            partyId: '1',
            partyName: 'ABC Corporation',
            advocateId: '2',
            advocateName: 'Sarah Davis',
            role: 'Lead',
          },
          {
            id: '2',
            partyId: '2',
            partyName: 'XYZ Industries',
            advocateId: '1',
            advocateName: 'John Williams',
            role: 'Lead',
          },
        ],
        createdAt: new Date().toISOString(),
      },
      {
        id: '2',
        caseNumber: 'CRI/2024/0001',
        caseType: 'Criminal',
        caseTitle: 'State vs John Doe',
        filingDate: '2024-02-10',
        courtId: '3',
        courtName: 'District Court - Central',
        judgeId: '3',
        judgeName: 'Hon. Judge Michael Brown',
        description: 'Criminal case for theft',
        status: 'Pending',
        parties: [
          {
            id: '3',
            partyName: 'State of Justice',
            partyType: 'Plaintiff',
            address: 'Government Building',
            contactNumber: '+1 (555) 502-0001',
            email: 'prosecutor@state.gov',
          },
          {
            id: '4',
            partyName: 'John Doe',
            partyType: 'Defendant',
            address: '789 Residential Lane',
            contactNumber: '+1 (555) 502-0002',
            email: 'j.doe@email.com',
            advocateId: '1',
            advocateName: 'John Williams',
          },
        ],
        advocateMappings: [
          {
            id: '3',
            partyId: '4',
            partyName: 'John Doe',
            advocateId: '1',
            advocateName: 'John Williams',
            role: 'Lead',
          },
        ],
        createdAt: new Date().toISOString(),
      },
    ];

    const mockHearings = [
      {
        id: '1',
        caseId: '1',
        caseNumber: 'CIV/2024/0001',
        caseTitle: 'Property Dispute - ABC vs XYZ',
        hearingDate: new Date().toISOString().split('T')[0],
        hearingTime: '10:00',
        courtId: '3',
        courtName: 'District Court - Central',
        judgeId: '3',
        judgeName: 'Hon. Judge Michael Brown',
        benchName: 'Courtroom 1',
        purpose: 'Initial hearing and case review',
        status: 'Scheduled',
        createdAt: new Date().toISOString(),
      },
      {
        id: '2',
        caseId: '2',
        caseNumber: 'CRI/2024/0001',
        caseTitle: 'State vs John Doe',
        hearingDate: new Date(Date.now() + 86400000).toISOString().split('T')[0],
        hearingTime: '14:00',
        courtId: '3',
        courtName: 'District Court - Central',
        judgeId: '3',
        judgeName: 'Hon. Judge Michael Brown',
        benchName: 'Courtroom 1',
        purpose: 'Bail hearing',
        status: 'Scheduled',
        createdAt: new Date().toISOString(),
      },
    ];

    const mockDocuments = [
      {
        id: '1',
        caseId: '1',
        caseNumber: 'CIV/2024/0001',
        documentType: 'Petition',
        documentName: 'Initial Petition - Property Dispute',
        description: 'Original petition filed by plaintiff',
        uploadDate: '2024-01-15',
        uploadedBy: 'clerk1',
        accessLevel: 'Public',
        fileUrl: '#document-1',
        createdAt: new Date().toISOString(),
      },
    ];

    const mockJudgments = [
      {
        id: '1',
        caseId: '1',
        caseNumber: 'CIV/2024/0001',
        caseTitle: 'Property Dispute - ABC vs XYZ',
        judgmentDate: '2024-03-20',
        judgmentType: 'Order',
        summary: 'Interim order issued for property inspection',
        status: 'Interim',
        orderUrl: '#judgment-1',
        createdAt: new Date().toISOString(),
      },
    ];

    setCourts(mockCourts);
    setJudges(mockJudges);
    setAdvocates(mockAdvocates);
    setUsers(mockUsers);
    setCases(mockCases);
    setHearings(mockHearings);
    setDocuments(mockDocuments);
    setJudgments(mockJudgments);
  };

  // Load data from localStorage on mount
  useEffect(() => {
    const savedUser = localStorage.getItem('ecourt_user');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
      setIsAuthenticated(true);
    }

    // Load all data from localStorage
    const savedCourts = localStorage.getItem('ecourt_courts');
    const savedJudges = localStorage.getItem('ecourt_judges');
    const savedAdvocates = localStorage.getItem('ecourt_advocates');
    const savedUsers = localStorage.getItem('ecourt_users');
    const savedCases = localStorage.getItem('ecourt_cases');
    const savedHearings = localStorage.getItem('ecourt_hearings');
    const savedDocuments = localStorage.getItem('ecourt_documents');
    const savedJudgments = localStorage.getItem('ecourt_judgments');
    const savedNotifications = localStorage.getItem('ecourt_notifications');
    const savedAuditLogs = localStorage.getItem('ecourt_audit_logs');

    // Check if this is first time load (no data or empty arrays in localStorage)
    let hasCourts = false;
    try {
      if (savedCourts) {
        const parsed = JSON.parse(savedCourts);
        hasCourts = Array.isArray(parsed) && parsed.length > 0;
      }
    } catch (e) {
      console.error('Error parsing courts data:', e);
    }
    
    if (!hasCourts) {
      // Initialize mock data on first load
      console.log('Initializing mock data...');
      initializeMockData();
    } else {
      // Load existing data from localStorage
      setCourts(JSON.parse(savedCourts));
      if (savedJudges) {
        const parsedJudges = JSON.parse(savedJudges);
        if (parsedJudges.length > 0) setJudges(parsedJudges);
      }
      if (savedAdvocates) {
        const parsedAdvocates = JSON.parse(savedAdvocates);
        if (parsedAdvocates.length > 0) setAdvocates(parsedAdvocates);
      }
      if (savedUsers) {
        const parsedUsers = JSON.parse(savedUsers);
        if (parsedUsers.length > 0) setUsers(parsedUsers);
      }
      if (savedCases) {
        const parsedCases = JSON.parse(savedCases);
        if (parsedCases.length > 0) setCases(parsedCases);
      }
      if (savedHearings) {
        const parsedHearings = JSON.parse(savedHearings);
        if (parsedHearings.length > 0) setHearings(parsedHearings);
      }
      if (savedDocuments) {
        const parsedDocuments = JSON.parse(savedDocuments);
        if (parsedDocuments.length > 0) setDocuments(parsedDocuments);
      }
      if (savedJudgments) {
        const parsedJudgments = JSON.parse(savedJudgments);
        if (parsedJudgments.length > 0) setJudgments(parsedJudgments);
      }
      if (savedNotifications) {
        const parsedNotifications = JSON.parse(savedNotifications);
        if (parsedNotifications.length > 0) setNotifications(parsedNotifications);
      }
      if (savedAuditLogs) {
        const parsedAuditLogs = JSON.parse(savedAuditLogs);
        if (parsedAuditLogs.length > 0) setAuditLogs(parsedAuditLogs);
      }
    }
  }, []);

  // Save to localStorage whenever data changes (only if data exists)
  useEffect(() => {
    if (courts.length > 0) {
      localStorage.setItem('ecourt_courts', JSON.stringify(courts));
    }
  }, [courts]);

  useEffect(() => {
    if (judges.length > 0) {
      localStorage.setItem('ecourt_judges', JSON.stringify(judges));
    }
  }, [judges]);

  useEffect(() => {
    if (advocates.length > 0) {
      localStorage.setItem('ecourt_advocates', JSON.stringify(advocates));
    }
  }, [advocates]);

  useEffect(() => {
    if (users.length > 0) {
      localStorage.setItem('ecourt_users', JSON.stringify(users));
    }
  }, [users]);

  useEffect(() => {
    if (cases.length > 0) {
      localStorage.setItem('ecourt_cases', JSON.stringify(cases));
    }
  }, [cases]);

  useEffect(() => {
    if (hearings.length > 0) {
      localStorage.setItem('ecourt_hearings', JSON.stringify(hearings));
    }
  }, [hearings]);

  useEffect(() => {
    if (documents.length > 0) {
      localStorage.setItem('ecourt_documents', JSON.stringify(documents));
    }
  }, [documents]);

  useEffect(() => {
    if (judgments.length > 0) {
      localStorage.setItem('ecourt_judgments', JSON.stringify(judgments));
    }
  }, [judgments]);

  useEffect(() => {
    if (notifications.length > 0) {
      localStorage.setItem('ecourt_notifications', JSON.stringify(notifications));
    }
  }, [notifications]);

  useEffect(() => {
    if (auditLogs.length > 0) {
      localStorage.setItem('ecourt_audit_logs', JSON.stringify(auditLogs));
    }
  }, [auditLogs]);

  const login = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
    localStorage.setItem('ecourt_user', JSON.stringify(userData));
    addAuditLog('User Login', `User ${userData.username} logged in`);
  };

  const logout = () => {
    addAuditLog('User Logout', `User ${user?.username} logged out`);
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('ecourt_user');
  };

  const addAuditLog = (action, details) => {
    const log = {
      id: Date.now().toString(),
      action,
      details,
      user: user?.username || 'System',
      timestamp: new Date().toISOString(),
    };
    setAuditLogs((prev) => [log, ...prev]);
  };

  const resetToMockData = () => {
    // Clear all localStorage
    localStorage.removeItem('ecourt_courts');
    localStorage.removeItem('ecourt_judges');
    localStorage.removeItem('ecourt_advocates');
    localStorage.removeItem('ecourt_users');
    localStorage.removeItem('ecourt_cases');
    localStorage.removeItem('ecourt_hearings');
    localStorage.removeItem('ecourt_documents');
    localStorage.removeItem('ecourt_judgments');
    localStorage.removeItem('ecourt_notifications');
    localStorage.removeItem('ecourt_audit_logs');
    
    // Initialize mock data
    initializeMockData();
    addAuditLog('System Reset', 'Mock data reinitialized');
  };

  const value = {
    user,
    isAuthenticated,
    login,
    logout,
    courts,
    setCourts,
    judges,
    setJudges,
    advocates,
    setAdvocates,
    users,
    setUsers,
    cases,
    setCases,
    hearings,
    setHearings,
    documents,
    setDocuments,
    judgments,
    setJudgments,
    notifications,
    setNotifications,
    auditLogs,
    addAuditLog,
    resetToMockData,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

